import React from 'react';
import { Box, Card } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { CustomTypography } from 'shared/ui/CustomTypography';
import {
  priceSummaryErrorState,
  priceSummaryLoadingState,
  priceSummaryState,
} from 'features/price-summary/state/priceSummaryAtoms';

const containerSx = {
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 1,
  height: '100%',
  justifyContent: 'space-between',
  paddingX: 1,
  paddingY: 0.5,
  width: '100%',
};

const valuesSx = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(180px, 1fr))' },
  width: { xs: '100%', md: 'auto' },
};

const cardSx = {
  alignItems: 'center',
  backgroundColor: '#2b2b2b',
  border: '1px solid #4b4b4b',
  boxSizing: 'border-box',
  display: 'flex',
  gap: 0.75,
  minHeight: 28,
  paddingX: 1,
  paddingY: 0.5,
};

const formatAmount = (value?: string): string => {
  if (!value) return '-';

  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;

  return Math.ceil(parsed).toLocaleString();
};

interface SummaryValueCardProps {
  label: string;
  value: string;
}

const SummaryValueCard: React.FC<SummaryValueCardProps> = ({ label, value }) => {
  return (
    <Card sx={cardSx}>
      <CustomTypography sx={{ color: '#c7c7c7', fontSize: 11 }}>{label}</CustomTypography>
      <CustomTypography sx={{ color: '#f5f8ff', fontSize: 12 }}>{value}</CustomTypography>
    </Card>
  );
};

export const PriceSummaryBar: React.FC = () => {
  const summary = useRecoilValue(priceSummaryState);
  const loading = useRecoilValue(priceSummaryLoadingState);
  const error = useRecoilValue(priceSummaryErrorState);

  const currency = summary?.currency ?? 'N/A';
  const fiscalYearToDate = `${formatAmount(summary?.fiscalYearToDateAmount)} ${currency}`;
  const currentMonthToDate = `${formatAmount(summary?.currentMonthToDateAmount)} ${currency}`;

  let statusText = '最新';
  let statusColor = '#9ad0ff';
  if (loading) {
    statusText = '更新中...';
  } else if (error !== '') {
    statusText = `取得失敗: ${error}`;
    statusColor = '#ffb4a9';
  }

  return (
    <Box component="div" sx={containerSx}>
      <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CustomTypography sx={{ color: '#f5f8ff', fontSize: 12 }}>
          Cost Summary
        </CustomTypography>
        <CustomTypography sx={{ color: statusColor, fontSize: 11 }}>
          {statusText}
        </CustomTypography>
      </Box>

      <Box component="div" sx={valuesSx}>
        <SummaryValueCard label="会計年度累計" value={fiscalYearToDate} />
        <SummaryValueCard label="当月累計" value={currentMonthToDate} />
      </Box>
    </Box>
  );
};
